(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('combo-builder')) {
    return;
  }

  var utils = window.ElazComboUtils;

  if (!utils) {
    return;
  }

  var hasAvailableVariant = utils.hasAvailableVariant;
  var availableVariantById = utils.availableVariantById;
  var createInitialSteps = utils.createInitialSteps;
  var stepsComplete = utils.stepsComplete;
  var buildCartItems = utils.buildCartItems;
  var comboRemovalUpdates = utils.comboRemovalUpdates;
  var DEFAULT_TRANSLATIONS = {
    previous: 'Previous',
    next: 'Next',
    add: 'Add combo to cart',
    progress: 'Step __current__ of __total__',
    build_title: 'Build your combo',
    item_count: '(__total__ items)',
    completed: '__current__ of __total__ configured',
    color: 'Color',
    size: 'Size',
    option: 'Option',
    gallery_image: 'View image __current__ of __total__',
    gallery_load_error: "We couldn't load the media for this color. Try again.",
    availability_unavailable: 'One of the options is no longer available. Review your selections and try again.',
    availability_unknown: 'Unable to confirm availability right now. Check your connection and try again.',
    invalid_configuration: 'This combo is not available at the moment.',
    added: 'Combo added to cart.',
    rollback_failed: 'Unable to remove the partially added combo. Refresh your cart before trying again.',
    add_failed: 'Unable to add the combo. Review your selections and try again.',
  };

  function createElement(tagName, options) {
    var element = document.createElement(tagName);
    var settings = options || {};

    if (settings.className) {
      element.className = settings.className;
    }
    if (settings.text !== undefined) {
      element.textContent = settings.text;
    }
    if (settings.type) {
      element.type = settings.type;
    }

    return element;
  }

  function textValue(value, fallback) {
    return typeof value === 'string' && value.trim() !== '' ? value : fallback;
  }

  function formatTranslation(template, values) {
    return Object.keys(values || {}).reduce(function (message, key) {
      return message.split('__' + key + '__').join(String(values[key]));
    }, template);
  }

  function readTranslations(element) {
    var source = element && element.querySelector ? element.querySelector('script[data-combo-translations]') : null;
    var translations = {};

    if (source) {
      try {
        translations = JSON.parse(source.textContent);
      } catch (error) {
        translations = {};
      }
    }

    return Object.keys(DEFAULT_TRANSLATIONS).reduce(function (result, key) {
      result[key] = translations && typeof translations[key] === 'string' && translations[key].trim() !== ''
        ? translations[key]
        : DEFAULT_TRANSLATIONS[key];
      return result;
    }, {});
  }

  function productJsonUrl(product) {
    if (!product || !product.url) {
      return null;
    }

    try {
      var url = new URL(product.url, window.location.origin);
      url.search = '';
      url.hash = '';
      if (!/\.js$/.test(url.pathname)) {
        url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      }
      return url.toString();
    } catch (error) {
      return null;
    }
  }

  function availabilityMessage(translations, state) {
    return state === 'unavailable'
      ? translations.availability_unavailable
      : translations.availability_unknown;
  }

  function ComboBuilder() {
    var element = Reflect.construct(HTMLElement, [], ComboBuilder);
    element.products = [];
    element.steps = [];
    element.activeStep = 0;
    element.submitting = false;
    element.submitted = false;
    element.galleryErrorActive = false;
    element.galleryRenderToken = 0;
    element.messageOwner = null;
    element.cartRecoveryRequired = false;
    return element;
  }

  ComboBuilder.prototype = Object.create(HTMLElement.prototype);
  ComboBuilder.prototype.constructor = ComboBuilder;

  ComboBuilder.prototype.connectedCallback = function () {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.translations = readTranslations(this);
    this.products = this.readProducts();
    this.stepContainer = this.querySelector('[data-combo-step]');
    this.progress = this.querySelector('[data-combo-progress]');
    this.progressBar = this.querySelector('[data-combo-progress-bar]');
    this.progressIndicator = this.querySelector('[data-combo-progress-indicator]');
    this.whatsapp = this.querySelector('[data-combo-whatsapp]');
    this.error = this.querySelector('[data-combo-error]');
    this.previousButton = this.querySelector('[data-combo-previous]');
    this.nextButton = this.querySelector('[data-combo-next]');
    this.gallery = this.querySelector('combo-media-gallery');
    this.productLink = this.querySelector('[data-combo-product-link]');
    this.productPrice = this.querySelector('[data-combo-product-price]');

    try {
      this.steps = createInitialSteps(Number(this.dataset.comboQuantity), this.products);
      if (this.gallery && typeof this.gallery.configure === 'function') {
        var builder = this;
        this.gallery.configure(this.products, {
          onError: function () {
            builder.showGalleryError(builder.t('gallery_load_error'));
          },
        });
      }
      this.bindEvents();
      this.bindSizebayLifecycle();
      this.render();
    } catch (error) {
      this.steps = [];
      this.showError(this.t('invalid_configuration'));
    }
  };

  ComboBuilder.prototype.readProducts = function () {
    var source = this.querySelector('script[data-combo-products]');

    if (!source) {
      return [];
    }

    try {
      var products = JSON.parse(source.textContent);
      return Array.isArray(products) ? products : [];
    } catch (error) {
      return [];
    }
  };

  ComboBuilder.prototype.t = function (key, values) {
    var translations = this.translations || DEFAULT_TRANSLATIONS;
    return formatTranslation(translations[key] || DEFAULT_TRANSLATIONS[key] || '', values);
  };

  ComboBuilder.prototype.bindEvents = function () {
    var self = this;

    this.addEventListener('change', function (event) {
      var input = event.target;

      if (!input || input.tagName !== 'INPUT' || input.type !== 'radio') {
        return;
      }

      if (input.hasAttribute('data-combo-product-index')) {
        self.selectProduct(Number(input.dataset.comboProductIndex));
      } else if (input.hasAttribute('data-combo-variant-id')) {
        self.selectVariant(input.dataset.comboVariantId);
      }
    });

    this.addEventListener('click', function (event) {
      var control = event.target && event.target.closest ? event.target.closest('[data-combo-previous], [data-combo-next], [data-combo-whatsapp]') : null;

      if (!control || !self.contains(control)) {
        return;
      }

      if (control.hasAttribute('data-combo-previous')) {
        self.goPrevious();
      } else if (control.hasAttribute('data-combo-next')) {
        self.goNext();
      }

      if (control.hasAttribute('data-combo-whatsapp') && control.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
      }
    });
  };

  ComboBuilder.prototype.getActiveStep = function () {
    return this.steps[this.activeStep] || null;
  };

  ComboBuilder.prototype.getActiveProduct = function () {
    var step = this.getActiveStep();
    return step ? this.products[step.productIndex] : null;
  };

  ComboBuilder.prototype.getShippingSimulationItems = function () {
    var shippingUtils = window.ElazProductShippingUtils;

    if (!shippingUtils || typeof shippingUtils.buildComboSimulationItems !== 'function') {
      return [];
    }

    return shippingUtils.buildComboSimulationItems(
      this.steps,
      this.products,
      Number(this.dataset.comboQuantity)
    );
  };

  ComboBuilder.prototype.selectProduct = function (productIndex) {
    if (this.submitting || !this.products[productIndex] || !hasAvailableVariant(this.products[productIndex])) {
      return;
    }

    var step = this.getActiveStep();
    if (!step) {
      return;
    }

    step.productIndex = productIndex;
    step.variantId = null;
    this.render();
  };

  ComboBuilder.prototype.selectVariant = function (variantId) {
    if (this.submitting) {
      return;
    }

    var step = this.getActiveStep();
    var product = this.getActiveProduct();
    var variant = availableVariantById(product, variantId);

    if (!step || !variant) {
      return;
    }

    step.variantId = variant.id;
    this.render();
  };

  ComboBuilder.prototype.goPrevious = function () {
    if (this.submitting || this.activeStep === 0) {
      return;
    }

    this.activeStep -= 1;
    this.render();
    this.focusStep();
  };

  ComboBuilder.prototype.goNext = function () {
    var step = this.getActiveStep();
    var product = this.getActiveProduct();

    if (this.submitting || this.submitted || this.cartRecoveryRequired || !step || !availableVariantById(product, step.variantId)) {
      return;
    }

    if (this.activeStep === this.steps.length - 1) {
      if (!stepsComplete(this.steps)) {
        var firstIncompleteStep = this.steps.findIndex(function (candidate) {
          return !candidate.variantId;
        });
        if (firstIncompleteStep >= 0) {
          this.activeStep = firstIncompleteStep;
          this.render();
          this.focusStep();
        }
        return;
      }
      this.submit();
      return;
    }

    this.activeStep += 1;
    this.render();
    this.focusStep();
  };

  ComboBuilder.prototype.render = function () {
    if (!this.stepContainer || !this.steps.length) {
      return;
    }

    var activeStep = this.getActiveStep();
    var activeProduct = this.getActiveProduct();
    var selectionIsValid = Boolean(activeStep && availableVariantById(activeProduct, activeStep.variantId));

    var focus = this.captureFocus();

    this.renderProgress();
    this.renderChoices();
    this.renderWhatsApp();
    this.renderProductLink();
    this.restoreFocus(focus);
    this.renderGallery();
    if (this.previousButton) {
      this.previousButton.disabled = this.submitting || this.activeStep === 0;
    }
    var selectionCanAdvance = this.activeStep === this.steps.length - 1
      ? stepsComplete(this.steps)
      : selectionIsValid;
    if (this.nextButton) {
      this.nextButton.disabled = this.submitting || this.submitted || this.cartRecoveryRequired || !selectionCanAdvance;
      this.nextButton.textContent = this.activeStep === this.steps.length - 1
        ? this.t('add')
        : this.t('next', { next: this.activeStep + 2 });
    }
    this.setAttribute('aria-busy', this.submitting ? 'true' : 'false');
  };

  ComboBuilder.prototype.renderWhatsApp = function () {
    if (!this.whatsapp) {
      return;
    }

    var step = this.getActiveStep();
    var product = this.getActiveProduct();
    var variant = step && product ? availableVariantById(product, step.variantId) : null;
    var phone = String(this.whatsapp.dataset.phone || '').replace(/\D/g, '');

    if (!variant || !phone) {
      this.whatsapp.setAttribute('aria-disabled', 'true');
      this.whatsapp.setAttribute('tabindex', '-1');
      this.whatsapp.removeAttribute('href');
      return;
    }

    var productUrl = variant.url || (product.url + '?variant=' + encodeURIComponent(variant.id));
    var absoluteUrl;
    try {
      absoluteUrl = new URL(productUrl, window.location.origin).toString();
    } catch (error) {
      absoluteUrl = productUrl;
    }
    var values = {
      produto: product.title || '',
      cor: product.colorName || '',
      variante: variant.title || '',
      preco: variant.priceText || new Intl.NumberFormat(
        (document.documentElement && document.documentElement.lang) || undefined,
        { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL' }
      ).format((Number(variant.price) || 0) / 100),
      link: absoluteUrl,
      sku: variant.sku || '',
    };
    var message = String(this.whatsapp.dataset.message || 'Olá! Tenho interesse neste produto: [produto]. Consegue me ajudar?\n\n[link]');
    Object.keys(values).forEach(function (token) {
      message = message.split('[' + token + ']').join(String(values[token]));
    });
    this.whatsapp.setAttribute('href', 'https://wa.me/' + phone + '?text=' + encodeURIComponent(message));
    this.whatsapp.setAttribute('target', '_blank');
    this.whatsapp.setAttribute('rel', 'noopener noreferrer');
    this.whatsapp.setAttribute('aria-disabled', 'false');
    this.whatsapp.removeAttribute('tabindex');
  };

  ComboBuilder.prototype.renderProductLink = function () {
    if (!this.productLink) {
      return;
    }

    var product = this.getActiveProduct();
    if (!product) {
      return;
    }

    this.productLink.setAttribute('href', product.url || '#');

    if (this.productPrice) {
      var step = this.getActiveStep();
      var variant = step ? availableVariantById(product, step.variantId) : null;
      var priceText = variant ? variant.priceText : (product.variants && product.variants[0] ? product.variants[0].priceText : '');
      this.productPrice.textContent = priceText ? priceText + '/un' : '';
    }
  };

  ComboBuilder.prototype.renderProgress = function () {
    var self = this;
    var completed = this.steps.filter(function (step) {
      return Boolean(availableVariantById(self.products[step.productIndex], step.variantId));
    }).length;
    var total = this.steps.length;
    var percent = total > 0 ? (completed / total) * 100 : 0;
    var status = this.t('completed', { current: completed, total: total });

    if (this.progress) {
      this.progress.textContent = status;
    }
    if (this.progressBar) {
      this.progressBar.setAttribute('aria-valuemax', String(total));
      this.progressBar.setAttribute('aria-valuenow', String(completed));
      this.progressBar.setAttribute('aria-valuetext', status);
    }
    if (this.progressIndicator) {
      this.progressIndicator.style.width = percent + '%';
    }
  };

  ComboBuilder.prototype.renderChoices = function () {
    var self = this;
    var step = this.getActiveStep();
    var product = this.getActiveProduct();
    var fragment = document.createDocumentFragment();

    var colors = createElement('fieldset');
    var colorLegend = createElement('legend', { text: this.t('color') });
    var sizes = createElement('fieldset');
    var sizeLegend = createElement('legend', { text: this.t('size') });

    colors.className = 'product-combo__colors';
    sizes.className = 'product-combo__sizes product-variant-picker__pill-list';

    var activeColorName = product ? textValue(product.colorName, textValue(product.title, '')) : '';
    if (activeColorName) {
      colorLegend.appendChild(document.createTextNode(': '));
      colorLegend.appendChild(createElement('span', { className: 'product-combo__color-name', text: activeColorName }));
    }
    colors.appendChild(colorLegend);
    this.products.forEach(function (candidate, index) {
      var id = 'combo-color-' + self.activeStep + '-' + index;
      var label = createElement('label');
      var input = createElement('input', { type: 'radio' });
      var colorName = textValue(candidate.colorName, textValue(candidate.title, self.t('option')));

      input.name = 'combo-color-' + self.activeStep;
      input.id = id;
      input.value = String(index);
      input.checked = step.productIndex === index;
      input.disabled = self.submitting || !hasAvailableVariant(candidate);
      input.setAttribute('data-combo-product-index', String(index));
      label.htmlFor = id;
      label.appendChild(input);

      var colorSwatch = candidate.colorSwatch || {};
      var swatch = null;
      if (colorSwatch.imageUrl) {
        swatch = createElement('img', { className: 'product-combo__color-swatch' });
        swatch.src = colorSwatch.imageUrl;
        swatch.alt = '';
        swatch.loading = 'lazy';
        swatch.decoding = 'async';
      } else if (colorSwatch.hex) {
        swatch = createElement('span', { className: 'product-combo__color-swatch' });
        swatch.style.backgroundColor = colorSwatch.hex;
      } else if (candidate.featuredImage && candidate.featuredImage.url) {
        swatch = createElement('img', { className: 'product-combo__color-swatch' });
        swatch.src = candidate.featuredImage.thumbnailUrl || candidate.featuredImage.url;
        swatch.alt = '';
        swatch.loading = 'lazy';
        swatch.decoding = 'async';
      }

      if (swatch) {
        swatch.setAttribute('aria-hidden', 'true');
        label.appendChild(swatch);
      }

      label.appendChild(createElement('span', { className: 'visually-hidden', text: colorName }));
      colors.appendChild(label);
    });

    sizes.id = 'ProductSelect-option-0';
    sizes.appendChild(sizeLegend);
    (product && Array.isArray(product.variants) ? product.variants : []).forEach(function (variant, index) {
      var id = 'combo-size-' + self.activeStep + '-' + index;
      var label = createElement('label');
      var input = createElement('input', { type: 'radio' });

      input.name = 'combo-size-' + self.activeStep;
      input.id = id;
      input.value = String(variant.id);
      input.checked = String(step.variantId) === String(variant.id);
      input.disabled = self.submitting || !variant.available;
      input.setAttribute('data-combo-variant-id', String(variant.id));
      label.className = 'pill pill--large pill--outline';
      label.classList.toggle('selected', input.checked);
      label.toggleAttribute('no-available', input.disabled);
      label.htmlFor = id;
      label.appendChild(input);
      label.appendChild(createElement('span', { className: 'pill__label', text: variant.title || self.t('size') }));
      sizes.appendChild(label);
    });

    // Keep SizeBay outside replaceChildren so step re-renders do not destroy it.
    var sizebayContainer = document.getElementById('szb-container');
    var heldSizebay = null;
    if (sizebayContainer) {
      heldSizebay = sizebayContainer;
      sizebayContainer.remove();
    }

    fragment.appendChild(colors);
    fragment.appendChild(sizes);
    this.stepContainer.replaceChildren(fragment);

    if (heldSizebay) {
      sizes.after(heldSizebay);
    }

    this.ensureSizebay();
  };

  ComboBuilder.prototype.bindSizebayLifecycle = function () {
    var builder = this;

    if (this.sizebayLifecycleBound) {
      return;
    }

    this.sizebayLifecycleBound = true;

    window.addEventListener('pageshow', function () {
      builder.sizebayRenderRequested = false;
      builder.ensureSizebay();
    });

    window.addEventListener('sizebay:loaded', function () {
      builder.sizebayRenderRequested = false;
      builder.ensureSizebay();
    });

    window.addEventListener('sizebay:rendered', function () {
      builder.placeSizebayContainer();
      builder.watchSizebayDuplicates();
      builder.clearSizebayRetry();
    });
  };

  ComboBuilder.prototype.watchSizebayDuplicates = function () {
    var builder = this;

    if (this.sizebayDuplicateObserver) {
      return;
    }

    this.sizebayDuplicateObserver = new MutationObserver(function () {
      var containers = document.querySelectorAll('#szb-container');
      var withButtons = 0;

      Array.prototype.forEach.call(containers, function (node) {
        if (node.querySelector('button')) {
          withButtons += 1;
        }
      });

      // Only clean when SizeBay left more than one rendered container.
      if (withButtons > 1 || (containers.length > 1 && withButtons === 1)) {
        builder.placeSizebayContainer();
      }
    });

    this.sizebayDuplicateObserver.observe(document.body, { childList: true, subtree: true });

    setTimeout(function () {
      if (!builder.sizebayDuplicateObserver) {
        return;
      }

      builder.sizebayDuplicateObserver.disconnect();
      builder.sizebayDuplicateObserver = null;
      builder.placeSizebayContainer();
    }, 4000);
  };

  ComboBuilder.prototype.sizebayPermalinkFor = function (product) {
    // SizeBay tenant 6439 catalogs products by numeric ID, matching prescript.js:
    // https://sejaelaz.com.br/products/{product.id}
    if (product && product.id) {
      return 'https://sejaelaz.com.br/products/' + product.id;
    }

    return 'https://sejaelaz.com.br' + window.location.pathname;
  };

  ComboBuilder.prototype.patchSizebayPrescript = function () {
    if (typeof window.SizebayPrescript !== 'function') {
      return false;
    }

    if (this.sizebayPatched) {
      return true;
    }

    var builder = this;
    var original = window.SizebayPrescript;

    window.SizebayPrescript = function () {
      var config = original();

      return Object.assign({}, config, {
        getPermalink: function () {
          return builder.sizebayPermalinkFor(builder.getActiveProduct());
        },
      });
    };

    this.sizebayPatched = true;
    return true;
  };

  ComboBuilder.prototype.clearSizebayRetry = function () {
    if (!this.sizebayRetryTimer) {
      return;
    }

    clearInterval(this.sizebayRetryTimer);
    this.sizebayRetryTimer = null;
  };

  ComboBuilder.prototype.placeSizebayContainer = function () {
    var anchor = document.getElementById('ProductSelect-option-0');
    var containers = Array.prototype.slice.call(document.querySelectorAll('#szb-container'));
    var withButtons = containers.filter(function (node) {
      return Boolean(node.querySelector('button'));
    });
    var keep = withButtons[0] || null;

    if (!anchor || !keep) {
      // Do not remove empty loading containers; SizeBay fills them asynchronously.
      return false;
    }

    containers.forEach(function (node) {
      if (node !== keep) {
        node.remove();
      }
    });

    // Keep a single VFR + chart pair if SizeBay appended duplicates.
    var seen = {};
    Array.prototype.forEach.call(keep.querySelectorAll('button'), function (button) {
      var key = button.id || button.textContent.trim();
      if (seen[key]) {
        button.remove();
        return;
      }
      seen[key] = true;
    });

    if (keep.previousElementSibling !== anchor) {
      anchor.after(keep);
    }

    return true;
  };

  ComboBuilder.prototype.implantSizebay = function () {
    if (this.placeSizebayContainer()) {
      return true;
    }

    if (!this.patchSizebayPrescript()) {
      return false;
    }

    // Boot SizeBay once. SizebayInit loads the module and renders on sizebay:loaded.
    if (!document.getElementById('szb-vfr__base')) {
      if (typeof window.SizebayInit === 'function') {
        window.SizebayInit();
        return true;
      }
      return false;
    }

    // Module already loaded: render at most once until buttons exist.
    if (this.sizebayRenderRequested) {
      return true;
    }

    if (typeof window.Sizebay === 'object' && typeof window.Sizebay.Implantation === 'function') {
      this.sizebayRenderRequested = true;
      var config = window.SizebayPrescript();
      window.Sizebay.Implantation({
        permalink: config.getPermalink(),
        tenantId: config.getTenantId(),
        buttons: config.getButtons(),
        anchor: config.getAnchor(),
        lang: config.getLanguage(),
        recommendation: config.getRecommendationText(),
      });
      return true;
    }

    return false;
  };

  ComboBuilder.prototype.ensureSizebay = function () {
    var builder = this;
    var permalink = this.sizebayPermalinkFor(this.getActiveProduct());
    var productChanged = Boolean(permalink && permalink !== this.sizebayPermalink);

    if (!document.getElementById('ProductSelect-option-0')) {
      return;
    }

    if (productChanged) {
      this.sizebayPermalink = permalink;
      this.sizebayRenderRequested = false;
    }

    if (!productChanged && this.placeSizebayContainer()) {
      this.clearSizebayRetry();
      this.watchSizebayDuplicates();
      return;
    }

    this.implantSizebay();
    this.watchSizebayDuplicates();

    if (this.sizebayRetryTimer) {
      return;
    }

    var attempts = 0;
    var maxAttempts = 40;

    this.sizebayRetryTimer = setInterval(function () {
      attempts += 1;

      if (builder.placeSizebayContainer()) {
        builder.sizebayRenderRequested = false;
        builder.clearSizebayRetry();
        return;
      }

      if (attempts === 8 || attempts === 16 || attempts === 28) {
        builder.sizebayRenderRequested = false;
        builder.implantSizebay();
      }

      if (attempts >= maxAttempts) {
        builder.clearSizebayRetry();
      }
    }, 250);
  };

  ComboBuilder.prototype.renderGallery = function () {
    var step = this.getActiveStep();
    if (!step || !this.gallery || typeof this.gallery.showProduct !== 'function') {
      return;
    }
    var builder = this;
    var renderToken = (this.galleryRenderToken || 0) + 1;
    this.galleryRenderToken = renderToken;
    return this.gallery.showProduct(step.productIndex).then(function () {
      if (renderToken === builder.galleryRenderToken && builder.galleryErrorActive) {
        builder.hideError();
      }
    }).catch(function () {});
  };

  ComboBuilder.prototype.setSubmitting = function (submitting) {
    this.submitting = submitting;
    this.setAttribute('aria-busy', submitting ? 'true' : 'false');
    this.querySelectorAll('input, button').forEach(function (control) {
      control.disabled = submitting || control.disabled;
    });

    if (!submitting) {
      this.render();
    }
  };

  ComboBuilder.prototype.captureFocus = function () {
    var focused = document.activeElement;

    if (!focused || !this.stepContainer || !this.stepContainer.contains(focused)) {
      return null;
    }

    if (focused.hasAttribute('data-combo-product-index')) {
      return { attribute: 'data-combo-product-index', value: focused.dataset.comboProductIndex };
    }
    if (focused.hasAttribute('data-combo-variant-id')) {
      return { attribute: 'data-combo-variant-id', value: focused.dataset.comboVariantId };
    }
    return null;
  };

  ComboBuilder.prototype.restoreFocus = function (focus) {
    if (!focus || !this.stepContainer) {
      return;
    }

    var target = Array.prototype.find.call(this.stepContainer.querySelectorAll('input'), function (input) {
      return !input.disabled && input.getAttribute(focus.attribute) === String(focus.value);
    });
    if (target) {
      target.focus();
    } else {
      this.focusStep();
    }
  };

  ComboBuilder.prototype.createInstanceId = function () {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'combo-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  };

  ComboBuilder.prototype.submit = async function () {
    if (this.submitting || this.submitted || this.cartRecoveryRequired || this.activeStep !== this.steps.length - 1) {
      return;
    }

    if (!stepsComplete(this.steps)) {
      var firstIncompleteStep = this.steps.findIndex(function (step) {
        return !step.variantId;
      });
      if (firstIncompleteStep >= 0) {
        this.activeStep = firstIncompleteStep;
        this.render();
        this.focusStep();
      }
      return;
    }

    var instanceId = this.createInstanceId();
    var properties = {
      _combo_instance_id: instanceId,
      _combo_name: this.dataset.comboName || '',
      _combo_quantity: String(this.steps.length),
      _combo_page: this.dataset.comboPage || window.location.pathname,
    };
    var items = buildCartItems(this.steps.map(function (step) {
      return step.variantId;
    }), properties);
    var routes = window.routes || {};

    this.setSubmitting(true);
    this.hideError();

    try {
      var availabilityState = await this.revalidateSteps();
      if (availabilityState !== 'current' || !stepsComplete(this.steps)) {
        this.setSubmitting(false);
        this.showError(availabilityMessage(this.translations || DEFAULT_TRANSLATIONS, availabilityState));
        this.focusStep();
        return;
      }

      var drawer = document.querySelector('cart-drawer');
      var requestBody = { items: items };
      if (drawer && drawer.dataset && drawer.dataset.sectionId) {
        requestBody.sections = drawer.dataset.sectionId;
        requestBody.sections_url = window.location.pathname;
      }

      var response = await window.fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('cart add failed');
      }

      if (!drawer) {
        this.dispatchEvent(new CustomEvent('combo:added', { bubbles: true, detail: { instanceId: instanceId } }));
        this.showMessage(this.t('added'));
        this.resetState();
      } else {
        try {
          var state = await response.json();
          await this.presentCartAddition(drawer, state);
          this.resetState();
        } catch (drawerError) {
          this.submitted = true;
          this.showMessage(this.t('added'));
        }
      }
      this.setSubmitting(false);
    } catch (error) {
      var rollbackCompleted = await this.rollback(instanceId);
      var availabilityState = await this.revalidateSteps();
      this.setSubmitting(false);
      if (!rollbackCompleted) {
        this.cartRecoveryRequired = true;
        this.render();
        this.showError(this.t('rollback_failed'));
      } else if (availabilityState !== 'current' || !stepsComplete(this.steps)) {
        this.showError(availabilityMessage(this.translations || DEFAULT_TRANSLATIONS, availabilityState));
        this.focusStep();
      } else {
        this.showError(this.t('add_failed'));
      }
    }
  };

  ComboBuilder.prototype.resetState = function () {
    this.steps.forEach(function (step) {
      step.variantId = null;
      step.imageId = null;
      step.selectedOptions = {};
    });
    this.activeStep = 0;
    this.submitted = false;
    this.cartRecoveryRequired = false;
    this.render();
  };

  ComboBuilder.prototype.rollback = async function (instanceId) {
    var routes = window.routes || {};
    var rootUrl = routes.root_url || '/';
    var cartUrl = rootUrl.replace(/\/?$/, '/') + 'cart.js';

    try {
      var cartResponse = await window.fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) {
        return false;
      }

      var cart = await cartResponse.json();
      var updates = comboRemovalUpdates(cart, instanceId);
      if (!Object.keys(updates).length) {
        return true;
      }

      var updateResponse = await window.fetch(routes.cart_update_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      });
      return updateResponse.ok;
    } catch (error) {
      return false;
    }
  };

  ComboBuilder.prototype.presentCartAddition = async function (drawer, state) {
    var sectionId = drawer && drawer.dataset ? drawer.dataset.sectionId : null;
    var items = state && state.items;
    var sectionHtml = sectionId && state && state.sections ? state.sections[sectionId] : null;

    if (
      !Array.isArray(items) ||
      items.length === 0 ||
      !sectionHtml ||
      typeof DOMParser !== 'function' ||
      !drawer ||
      typeof drawer.purchaseHandler !== 'function' ||
      typeof drawer.emit !== 'function'
    ) {
      throw new Error('invalid cart addition response');
    }

    var firstItem = items[0];
    var html = new DOMParser().parseFromString(sectionHtml, 'text/html');

    if (typeof drawer.updateAsideProductsByParsedState === 'function') {
      drawer.updateAsideProductsByParsedState(firstItem);
    }
    if (typeof drawer.updateEmptyStatus === 'function') {
      drawer.updateEmptyStatus();
    }

    await drawer.purchaseHandler(html, firstItem);
    items.slice(1).forEach(function (item) {
      drawer.emit('purchase', { parsedState: item });
    });
  };

  ComboBuilder.prototype.revalidateSteps = async function () {
    var self = this;

    try {
      var productIndexes = this.steps.reduce(function (indexes, step) {
        if (indexes.indexOf(step.productIndex) === -1) {
          indexes.push(step.productIndex);
        }
        return indexes;
      }, []);
      var refreshedProducts = await Promise.all(productIndexes.map(async function (productIndex) {
        var url = productJsonUrl(self.products[productIndex]);
        if (!url) {
          throw new Error('missing product URL');
        }

        var response = await window.fetch(url, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error('product refresh failed');
        }

        var product = await response.json();
        if (!product || !Array.isArray(product.variants)) {
          throw new Error('invalid product refresh');
        }
        return { productIndex: productIndex, variants: product.variants };
      }));

      refreshedProducts.forEach(function (freshProduct) {
        var oldVariants = self.products[freshProduct.productIndex].variants || [];
        self.products[freshProduct.productIndex] = Object.assign({}, self.products[freshProduct.productIndex], {
          variants: freshProduct.variants.map(function (freshVariant) {
            var oldVariant = oldVariants.find(function(ov) { return String(ov.id) === String(freshVariant.id); }) || {};
            return Object.assign({}, oldVariant, freshVariant);
          }),
        });
      });
      this.steps.forEach(function (step) {
        var product = self.products[step.productIndex];
        if (!availableVariantById(product, step.variantId)) {
          step.variantId = null;
        }
      });
      var firstIncompleteStep = this.steps.findIndex(function (step) {
        return !step.variantId;
      });
      if (firstIncompleteStep >= 0) {
        this.activeStep = firstIncompleteStep;
      }
      return stepsComplete(this.steps) ? 'current' : 'unavailable';
    } catch (error) {
      return 'unknown';
    }
  };

  ComboBuilder.prototype.showError = function (message) {
    this.galleryErrorActive = false;
    this.messageOwner = 'non-gallery';
    if (!this.error) {
      return;
    }

    this.error.hidden = false;
    this.error.removeAttribute('data-combo-state');
    this.error.setAttribute('role', 'alert');
    this.error.textContent = message;
    this.error.setAttribute('tabindex', '-1');
    this.error.focus();
  };

  ComboBuilder.prototype.showGalleryError = function (message) {
    if (this.error && !this.error.hidden && this.messageOwner !== 'gallery') {
      return;
    }
    this.showError(message);
    this.galleryErrorActive = true;
    this.messageOwner = 'gallery';
  };

  ComboBuilder.prototype.showMessage = function (message) {
    this.galleryErrorActive = false;
    this.messageOwner = 'non-gallery';
    if (!this.error) {
      return;
    }

    this.error.hidden = false;
    this.error.setAttribute('data-combo-state', 'success');
    this.error.setAttribute('role', 'status');
    this.error.removeAttribute('tabindex');
    this.error.textContent = message;
  };

  ComboBuilder.prototype.hideError = function () {
    this.galleryErrorActive = false;
    this.messageOwner = null;
    if (this.error) {
      this.error.hidden = true;
      this.error.removeAttribute('data-combo-state');
      this.error.setAttribute('role', 'alert');
      this.error.removeAttribute('tabindex');
      this.error.textContent = '';
    }
  };

  ComboBuilder.prototype.focusStep = function () {
    var target = this.stepContainer && this.stepContainer.querySelector('input:not(:disabled)');
    if (!target && this.progress) {
      this.progress.setAttribute('tabindex', '-1');
      target = this.progress;
    }
    if (target) {
      target.focus();
    }
  };

  window.customElements.define('combo-builder', ComboBuilder);
}());
